import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { ClientLayout } from './ClientLayout';

interface ClientProtectedRouteProps {
  children: React.ReactNode;
}

type UserRole = 'CLIENT' | 'PROSPECT' | 'ADMIN' | 'ATTORNEY' | 'CASE_MANAGER';

interface AuthState {
  isAuthenticated: boolean;
  isClientRole: boolean;
  isEnabled: boolean;
  isLoading: boolean;
}

/**
 * ClientProtectedRoute - Guards client-only routes
 * 
 * Validates:
 * 1. User is authenticated
 * 2. User role is CLIENT or PROSPECT
 * 3. User account is enabled (isEnabled: true)
 * 
 * Redirects:
 * - /signin if not authenticated
 * - /client/disabled if account disabled
 * - /dashboard if staff user attempts access
 * 
 * Wraps children in ClientLayout for consistent navigation
 * 
 * Requirements: 1.1, 1.3, 1.5, 1.7, 1.8
 */
export function ClientProtectedRoute({ children }: ClientProtectedRouteProps) {
  const location = useLocation();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isClientRole: false,
    isEnabled: true,
    isLoading: true,
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await getCurrentUser();
      
      // Check user role from Cognito session tokens (Requirement 1.8)
      const session = await fetchAuthSession();
      const userRole = session.tokens?.idToken?.payload['custom:role'] as UserRole;
      const isEnabled = session.tokens?.idToken?.payload['custom:isEnabled'] as string;

      // Check if user has client role (CLIENT or PROSPECT)
      const isClientRole = userRole === 'CLIENT' || userRole === 'PROSPECT';
      
      // Check if account is enabled (default to true if not set)
      const accountEnabled = isEnabled !== 'false';

      setAuthState({
        isAuthenticated: true,
        isClientRole,
        isEnabled: accountEnabled,
        isLoading: false,
      });
    } catch {
      setAuthState({
        isAuthenticated: false,
        isClientRole: false,
        isEnabled: true,
        isLoading: false,
      });
    }
  };

  // Show loading state while checking authentication
  if (authState.isLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect to signin if not authenticated (Requirement 1.1)
  if (!authState.isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Redirect staff users to dashboard (Requirement 1.7)
  if (!authState.isClientRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to disabled screen if account is disabled (Requirement 1.5)
  // Skip redirect if already on the disabled page to prevent infinite loop
  if (!authState.isEnabled && location.pathname !== '/client/disabled') {
    return <Navigate to="/client/disabled" replace />;
  }

  // Wrap children in ClientLayout for consistent navigation
  return <ClientLayout>{children}</ClientLayout>;
}
